import { z } from 'zod';
import { AuditActorSchema } from './audit';
import { AnswerIdSchema, AssessmentIdSchema, TenantIdSchema } from './ids';
import { isListShape, type PredicateObjectType, PREDICATE_ATTRIBUTES, shapeOf } from './predicate';
import { NonEmptyStringSchema, TimestampSchema } from './primitives';
import { isAbsenceMarker } from './provenance';

/**
 * What a client says when we ask them something (docs/DECISIONS.md ZD-064).
 *
 * ## Why this object exists at all, which is not obvious from the pipeline
 *
 * The state machine has allowed `clarification_required → canonical_model_created`
 * since sds-00, and ZD-049 §1 measured what that was worth: the transition is
 * real, `canonical_models` is one row per run to support it, and **nothing
 * anywhere could change the input**. There was no answers table in seven
 * migrations, no answer type, `Unknown.resolved_by_answer_id` was written by
 * nothing, and `checkExtractionIntegrity` rule 2 *refuses* an `answer_*`
 * citation outright — deliberately, because extraction runs before
 * clarification and an answer at that layer would be a fabrication.
 *
 * So the loop was representable, reachable, and could only ever re-run
 * byte-identical input. On the corpus that means the same 102 findings and the
 * same 50 deferrals, for ever. This is the object that makes the round mean
 * something, and it lands **beside** extraction rather than inside it, which is
 * what keeps that refusal intact and honest.
 *
 * ## An answer is not an extraction, and the difference is the whole design
 *
 * An extractor reads a document and reports what it says. A client *asserts*.
 * That is why an answer may do one thing extraction may not — see
 * `AnswerValueSchema`'s `none` — and why its provenance is
 * `asserted_by: {kind: 'user'}` with an `answer_*` citation carrying no
 * locator, which is exactly the branch ZD-015 built and left without a producer.
 */

/**
 * Every object type a question can be asked about.
 *
 * **Read off `PREDICATE_ATTRIBUTES`'s own keys rather than listed**, so it
 * cannot drift from the vocabulary the asks are drawn from — the discipline
 * `isListShape`'s comment describes, where a check took its scope from a
 * literal and silently stopped covering a shape added beside it (ZD-036 §7).
 */
export const ANSWER_OBJECT_TYPES = Object.keys(PREDICATE_ATTRIBUTES) as [
  PredicateObjectType,
  ...PredicateObjectType[],
];
export const AnswerObjectTypeSchema = z.enum(ANSWER_OBJECT_TYPES);

/**
 * The three things a client can say, and they are three rather than two.
 *
 * **`none` is the one an extractor may not say.** ZD-036 §2 forbids extraction
 * establishing an empty collection, because a document's silence is not a
 * denial and one extra map entry turns *"nobody said where this personal data
 * lives"* into *"it lives nowhere"* — CLAUDE.md principle 9's failure mode
 * reachable by a spelling-level edit. A client saying *"that flow carries no
 * personal data"* is not silence; it is the assertion that rule was reserving,
 * and that entry's own reopen note says so: *"a clarification answer can assert
 * an absence directly — 'nothing else holds this' is a real answer a client can
 * give, and it arrives with an `answer_*` citation rather than an artefact
 * one."* This is that. It matters at scale rather than in principle: **122 of
 * the corpus's 219 asks name a collection**, so without it the commonest client
 * response to the commonest question is unsayable.
 *
 * **`unanswerable` is ZD-019's question asked of the answer rather than of the
 * fact.** *What does a client write when they do not know?* With only `stated`
 * and `none` they must pick one, and a schema that makes a person guess
 * manufactures an assertion exactly as a required field does. It is also what
 * lets the clarification loop terminate **by record** rather than by exhausting
 * `MAX_CLARIFICATION_ROUNDS`: a deferral whose question came back unanswerable
 * is discharged, and the reviewer decides on what there is.
 *
 * ## The two refusals that keep the three distinguishable
 *
 * Both are the family CLAUDE.md tracks — two representations of one state,
 * agreeing on all current data until they do not — caught before anything
 * crossed:
 *
 *  - a `stated` value may not be an **absence marker**. `'unknown'`, `''`,
 *    `null` and `[]` all mean *nobody said*, and `unanswerable` is the only way
 *    to say it. Without this refusal a client's *"I don't know"* could arrive as
 *    either, and nothing downstream could tell which record meant what.
 *  - `none` is refused on anything that is not a list. A scalar already has a
 *    stated negative — `encrypted: false` is a claim, not an absence
 *    (`isAbsenceMarker` says so in terms) — so `none` beside it would be a
 *    second spelling of `stated: false`.
 */
export const AnswerValueSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('stated'),
    /**
     * Checked against `shapeOf(object_type, attribute)` by the refinement
     * below, never here: the vocabulary lives in `PREDICATE_ATTRIBUTES` and a
     * second copy of it would be a second rule (ZD-029 §1).
     */
    value: z.union([z.string(), z.boolean(), z.array(z.string())]),
  }),
  /** Established as empty. A denial, and only a client may make it. */
  z.strictObject({ kind: z.literal('none') }),
  /**
   * Asked, and the client cannot say. Establishes **nothing** on the model —
   * writing an attribute here would be the manufactured assertion this member
   * exists to prevent.
   */
  z.strictObject({ kind: z.literal('unanswerable'), reason: NonEmptyStringSchema }),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

const AnswerShape = z.strictObject({
  answer_id: AnswerIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,

  /** The clarification round this was given in, so a re-run can be attributed. */
  round: z.int().positive(),

  /**
   * What the question was about.
   *
   * `(object_type, attribute)` indexes `PREDICATE_ATTRIBUTES` — the same closed
   * vocabulary the asks are drawn from, so an answer cannot name something no
   * question could have asked. `object_id` is the canonical object, or
   * `SYSTEM_OBJECT_ID` for the system itself.
   *
   * There is deliberately **no `question_id`**. `(object_id, attribute)` *is*
   * the question's identity — `asksAsRefs` returns exactly that pair — so a
   * separate reference would be a second record of one fact, and the two could
   * disagree about which question an answer answers (ZD-017).
   */
  object_type: AnswerObjectTypeSchema,
  object_id: NonEmptyStringSchema,
  attribute: NonEmptyStringSchema,

  value: AnswerValueSchema,

  /**
   * Who answered. Kind `user`, and it is the point rather than a formality: the
   * whole reason an answer may establish an empty collection where extraction
   * may not is that a person asserted it.
   */
  answered_by: AuditActorSchema,
  answered_at: TimestampSchema,
});

export const AnswerSchema = AnswerShape.superRefine((answer, ctx) => {
  if (answer.answered_by.kind !== 'user') {
    ctx.addIssue({
      code: 'custom',
      message:
        `an answer must be given by an actor of kind "user"; got "${answer.answered_by.kind}". ` +
        'A worker that could answer its own question would be establishing a fact from nothing.',
      path: ['answered_by', 'kind'],
    });
  }

  const shape = shapeOf(answer.object_type, answer.attribute);
  if (shape === undefined) {
    ctx.addIssue({
      code: 'custom',
      message:
        `no attribute "${answer.attribute}" on a ${answer.object_type}; an answer may only ` +
        'name something a question could have asked',
      path: ['attribute'],
    });
    return;
  }

  if (answer.value.kind === 'none' && !isListShape(shape)) {
    ctx.addIssue({
      code: 'custom',
      message:
        `"${answer.attribute}" is not a collection, so "none" is a second spelling of a stated ` +
        'negative; state the value instead',
      path: ['value', 'kind'],
    });
    return;
  }

  if (answer.value.kind !== 'stated') return;

  const { value } = answer.value;
  if (isAbsenceMarker(value)) {
    ctx.addIssue({
      code: 'custom',
      message:
        `${JSON.stringify(value)} means nobody said, which is what "unanswerable" is for; a ` +
        'stated answer must state something',
      path: ['value', 'value'],
    });
    return;
  }

  // Shape conformance, read through `shapeOf` so this cannot drift from the
  // vocabulary the predicate and the clarification both use.
  if (shape.kind === 'scalar' && !(shape.values as readonly unknown[]).includes(value)) {
    ctx.addIssue({
      code: 'custom',
      message: `"${answer.attribute}" takes one of ${shape.values.map(String).join(', ')}`,
      path: ['value', 'value'],
    });
  }
  if (shape.kind === 'text' && typeof value !== 'string') {
    ctx.addIssue({
      code: 'custom',
      message: `"${answer.attribute}" is prose`,
      path: ['value', 'value'],
    });
  }
  if (shape.kind === 'reference' && typeof value !== 'string') {
    ctx.addIssue({
      code: 'custom',
      message: `"${answer.attribute}" is a single reference`,
      path: ['value', 'value'],
    });
  }
  if (isListShape(shape) && !Array.isArray(value)) {
    ctx.addIssue({
      code: 'custom',
      message: `"${answer.attribute}" is a collection; use "none" to say it is empty`,
      path: ['value', 'value'],
    });
  }
  if (
    shape.kind === 'enum_collection' &&
    Array.isArray(value) &&
    value.some((member) => !shape.values.includes(member))
  ) {
    ctx.addIssue({
      code: 'custom',
      message: `"${answer.attribute}" takes members of ${shape.values.join(', ')}`,
      path: ['value', 'value'],
    });
  }
});
export type Answer = z.infer<typeof AnswerShape>;

/** The pair that identifies the question an answer answers. */
export const answerRef = (answer: Answer): string => `${answer.object_id}|${answer.attribute}`;

/**
 * Whether an answer puts anything on the canonical model.
 *
 * `unanswerable` is recorded and establishes nothing, so this is the one place
 * that distinction is made and every caller reads it rather than re-deriving
 * `kind !== 'unanswerable'` (ZD-027 rule 3).
 */
export const answerEstablishes = (answer: Answer): boolean => answer.value.kind !== 'unanswerable';
